// client/pages/admin/CurrencyCountries.tsx
// T-15 — the currency/country matrix. One row per country: the currency it is
// priced in and whether the market is switched ON. A disabled country is refused
// by the captive portal (and any other market-gated flow). Unmapped countries
// fall back to the default currency.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Globe, Loader2, AlertCircle, Check, RefreshCw, Trash2, Plus } from "lucide-react";
import {
  getCurrencyCountries,
  upsertCurrencyCountry,
  deleteCurrencyCountry,
  CurrencyCountry,
} from "../../api/services/adminCommercialService";

const fld =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

export default function AdminCurrencyCountries() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<CurrencyCountry[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState("USD");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [draft, setDraft] = useState({ country_code: "", currency: "", enabled: true });

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await getCurrencyCountries();
      res.rows.sort((a, b) => a.country_code.localeCompare(b.country_code));
      setRows(res.rows);
      setDefaultCurrency(res.defaultCurrency);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.currencyMarkets.errors.load", { defaultValue: "Failed to load the currency matrix" }));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const setField = (code: string, field: keyof CurrencyCountry, value: any) =>
    setRows((prev) => prev.map((r) => (r.country_code === code ? { ...r, [field]: value } : r)));

  const save = async (row: CurrencyCountry) => {
    const code = (row.country_code || "").toUpperCase().trim();
    const currency = (row.currency || "").toUpperCase().trim();
    if (!/^[A-Z]{2}$/.test(code)) {
      setErr(t("admin.currencyMarkets.errors.badCountryRow", { defaultValue: "{{code}}: country code must be ISO-2", code: code || t("admin.currencyMarkets.blank", { defaultValue: "(blank)" }) }));
      return;
    }
    if (!/^[A-Z]{3}$/.test(currency)) {
      setErr(t("admin.currencyMarkets.errors.badCurrencyRow", { defaultValue: "{{code}}: currency must be ISO-3", code }));
      return;
    }
    setBusy(code);
    setErr("");
    setSaved(null);
    try {
      await upsertCurrencyCountry({ country_code: code, currency, enabled: row.enabled });
      setSaved(code);
      setTimeout(() => setSaved(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.currencyMarkets.errors.save", { defaultValue: "Failed to save" }));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row: CurrencyCountry) => {
    if (!row._id) return;
    setBusy(row.country_code);
    setErr("");
    try {
      await deleteCurrencyCountry(row._id);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.currencyMarkets.errors.delete", { defaultValue: "Failed to delete" }));
    } finally {
      setBusy(null);
    }
  };

  const add = async () => {
    const code = draft.country_code.toUpperCase().trim();
    const currency = draft.currency.toUpperCase().trim();
    if (!/^[A-Z]{2}$/.test(code)) {
      setErr(t("admin.currencyMarkets.errors.enterCountry", { defaultValue: "Enter an ISO-2 country code (e.g. NG, CI, CG)" }));
      return;
    }
    if (!/^[A-Z]{3}$/.test(currency)) {
      setErr(t("admin.currencyMarkets.errors.enterCurrency", { defaultValue: "Enter an ISO-3 currency (e.g. NGN, XOF, XAF)" }));
      return;
    }
    setBusy("new");
    setErr("");
    try {
      await upsertCurrencyCountry({ country_code: code, currency, enabled: draft.enabled });
      setDraft({ country_code: "", currency: "", enabled: true });
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || t("admin.currencyMarkets.errors.add", { defaultValue: "Failed to add" }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Globe className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold">{t("admin.currencyMarkets.title", { defaultValue: "Currency & Markets" })}</h1>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <RefreshCw className="w-4 h-4" /> {t("admin.currencyMarkets.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        {t("admin.currencyMarkets.description", {
          defaultValue:
            "The serving currency for each country and whether its market is switched on. A disabled country is refused by the captive portal. Countries with no row fall back to {{defaultCurrency}}.",
          defaultCurrency,
        })}
      </p>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <Card className="p-4 mb-6">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("admin.currencyMarkets.countryLabel", { defaultValue: "Country (ISO-2)" })}</label>
            <input
              className={fld}
              placeholder="NG"
              maxLength={2}
              value={draft.country_code}
              onChange={(e) => setDraft({ ...draft, country_code: e.target.value.toUpperCase() })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t("admin.currencyMarkets.currencyLabel", { defaultValue: "Currency (ISO-3)" })}</label>
            <input
              className={fld}
              placeholder="NGN"
              maxLength={3}
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm pb-2">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            {t("admin.currencyMarkets.enabled", { defaultValue: "Enabled" })}
          </label>
          <button
            onClick={add}
            disabled={busy === "new"}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {busy === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("admin.currencyMarkets.addUpdate", { defaultValue: "Add / Update" })}
          </button>
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-500">{t("admin.currencyMarkets.empty", { defaultValue: "No country mappings yet." })}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-4">{t("admin.currencyMarkets.table.country", { defaultValue: "Country" })}</th>
                <th className="py-2 pr-4">{t("admin.currencyMarkets.table.currency", { defaultValue: "Currency" })}</th>
                <th className="py-2 pr-4">{t("admin.currencyMarkets.table.market", { defaultValue: "Market" })}</th>
                <th className="py-2 pr-4 text-right">{t("admin.currencyMarkets.table.actions", { defaultValue: "Actions" })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.country_code} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-4 font-mono">{row.country_code}</td>
                  <td className="py-2 pr-4">
                    <input
                      className={`${fld} max-w-[8rem]`}
                      value={row.currency}
                      onChange={(e) => setField(row.country_code, "currency", e.target.value.toUpperCase())}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <label className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!!row.enabled}
                        onChange={(e) => setField(row.country_code, "enabled", e.target.checked)}
                      />
                      {row.enabled
                        ? t("admin.currencyMarkets.enabled", { defaultValue: "Enabled" })
                        : t("admin.currencyMarkets.disabled", { defaultValue: "Disabled" })}
                    </label>
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => save(row)}
                        disabled={busy === row.country_code}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                      >
                        {busy === row.country_code ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : saved === row.country_code ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : null}
                        {saved === row.country_code
                          ? t("admin.currencyMarkets.saved", { defaultValue: "Saved" })
                          : t("admin.currencyMarkets.save", { defaultValue: "Save" })}
                      </button>
                      <button
                        onClick={() => remove(row)}
                        disabled={busy === row.country_code}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> {t("admin.currencyMarkets.delete", { defaultValue: "Delete" })}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
